import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FiSave, FiX, FiUpload, FiDownload, FiUserMinus } from "react-icons/fi";
import { getEmployees, getLookups } from "../../../services/employeeApi";
import { getCycleMembers, updateCycleMembers } from "../../../services/cycleApi";
import { Modal, Button, SearchInput, Loader, Banner, OrgFilters, ConfirmDialog } from "../../../components/common";
import useOrgFilters from "../../../hooks/useOrgFilters"; // [NEW] shared organisation filters
import { singleValue, toList, matchesOrg } from "../../../utils/orgFilters"; // PM-18: filters hold lists
import { getErrorMessage } from "../../../utils/errors";
import { sortAlpha } from "../../../utils/alphaSort"; // [NEW] A to Z
import { readSpreadsheetFile } from "../../../utils/readSpreadsheet"; // [NEW] CSV / Excel import
import { downloadXlsx } from "../../../utils/exportExcel";
import { downloadCsv } from "../../../utils/exportCsv";

const api = { getEmployees, getLookups, getCycleMembers, updateCycleMembers };

const NONE = "__none__"; // employees without a cost center (used to group the Selected list)
const uniq = (values) => [...new Set(values.filter(Boolean))];

// [NEW] CSV / Excel import: the file needs one column that identifies the employee. Header names are matched loosely
// (case, spaces, dashes and underscores are ignored), e.g. "employee_id", "Employee ID", "Emp Code", "Email".
const norm = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const ID_HEADERS = ["employeeid", "empid", "employeecode", "empcode", "employeeno", "employeenumber", "id"];
const EMAIL_HEADERS = ["email", "officialemail", "workemail", "emailid", "emailaddress"];
const TEMPLATE_COLUMNS = [{ key: "employee_id", label: "employee_id" }];
const TEMPLATE_ROWS = [{ employee_id: "E1001" }, { employee_id: "E1002" }];

// Turns the rows of the file into a list of { value, kind: "id" | "email", line }.
function extractKeys(rows) {
  const headers = Object.keys(rows[0] || {});
  const idHeader = headers.find((h) => ID_HEADERS.includes(norm(h)));
  const emailHeader = headers.find((h) => EMAIL_HEADERS.includes(norm(h)));
  if (!idHeader && !emailHeader) return null;
  return rows
    .map((r, i) => {
      const id = idHeader ? String(r[idHeader] || "").trim() : "";
      const email = emailHeader ? String(r[emailHeader] || "").trim() : "";
      return id ? { value: id, kind: "id", line: i + 2 } : email ? { value: email, kind: "email", line: i + 2 } : null;
    })
    .filter(Boolean);
}

// [NEW] Employees of a cycle, with two dropdowns: Department and Cost center (employee master).
// Simple rule: ticked = in the cycle, unticked = not in the cycle. Tick to add, untick to take someone out:
// - choose a department or cost center, use "Select all" or tick / untick single people,
// - choose another department or cost center and continue; ticks are kept,
// - Save changes adds the new people and takes the unticked ones out (eligibility EXCLUDED).
// A person taken out keeps their record; ticking them again brings them back (eligibility ELIGIBLE).
// [NEW] "Deselect all employees" unticks everyone in one go; "Show employees not in the cycle" lists the unticked people.
export default function CycleEmployeePicker({ cycle, onClose, onDone }) {
  const [people, setPeople] = useState(null);
  const [members, setMembers] = useState(new Map());
  const [lookups, setLookups] = useState(null);
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({ search: "" });
  const org = useOrgFilters({ list: people }); // [NEW] Business Unit, Department, Location, Cost Centre, Legal Entity (all can be combined)
  const [groupBy, setGroupBy] = useState("department");
  const [applyTemplates, setApplyTemplates] = useState(true);
  const [busy, setBusy] = useState(false);
  const [onlyInCycle, setOnlyInCycle] = useState(false); // [NEW] list only the ticked people (no filter needed)
  const [onlyNotInCycle, setOnlyNotInCycle] = useState(false); // [NEW] list only the people who are NOT in the cycle (unticked)
  const [confirmClear, setConfirmClear] = useState(false); // [NEW] confirm "Deselect all employees"
  const [report, setReport] = useState(null); // [NEW] result of the last CSV / Excel import
  const fileRef = useRef(null);

  useEffect(() => {
    if (!cycle) return;
    setPeople(null);
    setFilters({ search: "" });
    setReport(null);
    setOnlyInCycle(false);
    setOnlyNotInCycle(false);
    org.clear();
    Promise.all([api.getEmployees({}, { fresh: true }), api.getCycleMembers(cycle.id), api.getLookups()])
      .then(([emp, mem, look]) => {
        const map = new Map(mem.data.map((m) => [m.employeeId, m]));
        // Active employees, plus anyone already in the cycle (even if inactive now), so the list matches the cycle.
        setPeople(emp.data.filter((e) => e.status === "Active" || map.has(e.id)));
        setMembers(map);
        setSelected(mem.data.filter((m) => m.eligibility === "ELIGIBLE").map((m) => m.employeeId));
        setLookups(look.data);
      })
      .catch((err) => { toast.error(getErrorMessage(err)); setPeople([]); });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- [FIX] org.clear is stable; reload only when the cycle changes
  }, [cycle, org.clear]);

  const all = useMemo(() => people || [], [people]);
  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));
  const costOf = (p) => p.costCenter || NONE;
  const member = (p) => members.get(p.id) || null;
  const inCycle = (p) => member(p)?.eligibility === "ELIGIBLE";
  const launched = cycle && cycle.status !== "Draft";

  // Why a person's tick cannot be changed (null = it can).
  const lockReason = (p) => {
    const m = member(p);
    if (m?.finalized) return "Result finalized";
    if (m && !["ELIGIBLE", "EXCLUDED"].includes(m.eligibility)) return m.eligibility === "EXITED" ? "Exited" : "Not eligible";
    if (!m && p.status !== "Active") return "Inactive";
    if (!m && launched && !p.managerId) return "No manager";
    return null;
  };
  const canChange = (p) => !lockReason(p);

  const departments = (lookups?.departments || []).filter((d) => all.some((p) => p.departmentId === d.id));
  const costCenters = [
    ...uniq(all.map((p) => p.costCenter)).sort((a, b) => a.localeCompare(b)).map((c) => ({ id: c, name: c })),
    ...(all.some((p) => !p.costCenter) ? [{ id: NONE, name: "No cost center" }] : []),
  ];
  const deptName = departments.find((d) => d.id === singleValue(org.filters.departmentId))?.name;
  const costName = costCenters.find((c) => c.id === singleValue(org.filters.costCenter, { allowNone: true }))?.name;

  const q = filters.search.trim().toLowerCase();
  const chosenFilter = Boolean(org.active || q || onlyInCycle || onlyNotInCycle);
  const shown = !chosenFilter ? [] : sortAlpha(all.filter((p) => org.matches(p)
    && (!onlyInCycle || selected.includes(p.id))
    && (!onlyNotInCycle || !selected.includes(p.id)) // [NEW] only employees who are not in the cycle
    && (!q || p.name.toLowerCase().includes(q) || p.id.toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q))
    ));
  const changeable = shown.filter(canChange);
  const tickedHere = changeable.filter((p) => selected.includes(p.id)).length;
  const allHere = changeable.length > 0 && tickedHere === changeable.length;

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAllHere = () => setSelected((s) => (allHere
    ? s.filter((id) => !changeable.some((p) => p.id === id))
    : uniq([...s, ...changeable.map((p) => p.id)])));

  // [NEW] Deselect ALL employees of the cycle in one click. "Delete" here means untick (= take out of the cycle).
  // People who cannot be changed (result finalized, exited, ...) stay ticked. Nothing is saved until "Save changes".
  const clearable = all.filter((p) => selected.includes(p.id) && canChange(p));
  const lockedTicked = selected.length - clearable.length;
  const deselectAll = () => {
    const drop = new Set(clearable.map((p) => p.id));
    setSelected((s) => s.filter((id) => !drop.has(id)));
    setConfirmClear(false);
    toast.info(`${drop.size} employee${drop.size === 1 ? "" : "s"} deselected. Click Save changes to take them out of the cycle.`);
  };

  // [NEW] The two "show" options work as a pair: only one can be on at a time.
  const showInCycle = (on) => { setOnlyInCycle(on); if (on) setOnlyNotInCycle(false); };
  const showNotInCycle = (on) => { setOnlyNotInCycle(on); if (on) setOnlyInCycle(false); };

  // [NEW] Read a CSV / Excel file and tick the employees listed in it.
  // Nothing is saved until "Save changes", so the result can still be reviewed and corrected.
  const pickFile = () => fileRef.current?.click();
  const importFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const rows = await readSpreadsheetFile(file);
      if (!rows.length) { setReport({ error: "The file has no rows under the header." }); return; }
      const keys = extractKeys(rows);
      if (!keys) { setReport({ error: "No employee column found. The first row must contain a column named employee_id (or email)." }); return; }
      const byId = new Map(all.map((p) => [p.id.toLowerCase(), p]));
      const byEmail = new Map(all.filter((p) => p.email).map((p) => [p.email.toLowerCase(), p]));
      const seen = new Set();
      const next = new Set(selected);
      const out = { fileName: file.name, total: keys.length, applied: 0, unchanged: 0, duplicates: 0, locked: [], unmatched: [] };
      keys.forEach((k) => {
        const p = k.kind === "id" ? byId.get(k.value.toLowerCase()) : byEmail.get(k.value.toLowerCase());
        if (!p) { out.unmatched.push(k.value); return; }
        if (seen.has(p.id)) { out.duplicates += 1; return; }
        seen.add(p.id);
        const lock = lockReason(p);
        if (lock) { out.locked.push(`${p.name} (${p.id}): ${lock}`); return; }
        if (next.has(p.id)) { out.unchanged += 1; return; }
        next.add(p.id);
        out.applied += 1;
      });
      setSelected([...next]);
      setReport(out);
      if (out.applied) toast.success(`${out.applied} employee${out.applied === 1 ? "" : "s"} ticked from ${file.name}. Click Save changes to apply.`);
      else toast.info("No employees were changed by this file.");
    } catch (err) {
      setReport({ error: getErrorMessage(err) });
    }
  };
  const downloadTemplate = (format) => {
    if (format === "csv") downloadCsv("cycle-employees-import-template", TEMPLATE_COLUMNS, TEMPLATE_ROWS);
    else downloadXlsx("cycle-employees-import-template", [{ name: "Employees", columns: TEMPLATE_COLUMNS, rows: TEMPLATE_ROWS }]);
  };

  // [UPDATED] Filter checkbox = add / remove from the cycle selection (both directions):
  // - SELECT a value (e.g. a department): everyone it brings into the list is ticked (= added to the cycle).
  // - UNSELECT that value: the people that value had brought in are unticked again (= not in the cycle).
  // People who cannot be changed (result finalized, exited ...) are skipped both ways.
  // Nothing is saved until "Save changes".
  const orgUi = {
    ...org,
    set: (key, value) => {
      const before = toList(org.filters[key]);
      const after = toList(value);
      const added = after.filter((v) => !before.includes(v));
      const removed = before.filter((v) => !after.includes(v));
      const oldFilters = org.filters;
      org.set(key, value);
      if (!added.length && !removed.length) return;
      const searchOk = (p) => !q || p.name.toLowerCase().includes(q) || p.id.toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q);

      // people the newly chosen values bring in -> tick
      const next = { ...oldFilters, [key]: value };
      const fresh = added.length
        ? all.filter((p) => matchesOrg(p, next) && canChange(p) && !selected.includes(p.id) && searchOk(p))
        : [];
      // people that the un-chosen values had brought in (they matched the old filters and carry the removed value) -> untick
      const drop = removed.length
        ? all.filter((p) => matchesOrg(p, oldFilters) && matchesOrg(p, { [key]: removed }) && canChange(p) && selected.includes(p.id) && searchOk(p))
        : [];
      if (!fresh.length && !drop.length) return;

      const dropIds = new Set(drop.map((p) => p.id));
      setSelected((s) => uniq([...s.filter((id) => !dropIds.has(id)), ...fresh.map((p) => p.id)]));
      const n = (c) => `${c} employee${c === 1 ? "" : "s"}`;
      if (fresh.length) toast.info(`${n(fresh.length)} ticked. Click Save changes to add them to the cycle.`);
      if (drop.length) toast.info(`${n(drop.length)} unticked. Click Save changes to take them out of the cycle.`);
    },
  };

  // [NEW] How many of each filter option's people are ticked (= in the cycle) out of all of them, so the dropdowns show where people are already added.
  const marks = useMemo(() => {
    const out = { businessUnit: {}, departmentId: {}, location: {}, costCenter: {}, legalEntity: {} };
    const ticked = new Set(selected);
    all.forEach((p) => {
      Object.keys(out).forEach((key) => {
        const value = String(p[key] ?? "").trim() || NONE;
        const slot = out[key][value] || (out[key][value] = { done: 0, total: 0 });
        slot.total += 1;
        if (ticked.has(p.id)) slot.done += 1;
      });
    });
    return out;
  }, [all, selected]);

  // What Save will do.
  const toAdd = all.filter((p) => selected.includes(p.id) && !member(p));
  const toRestore = all.filter((p) => selected.includes(p.id) && member(p)?.eligibility === "EXCLUDED");
  const toRemove = all.filter((p) => !selected.includes(p.id) && inCycle(p) && canChange(p));
  const changes = toAdd.length + toRestore.length + toRemove.length;
  const chosen = all.filter((p) => selected.includes(p.id));
  const groups = groupBy === "costCenter"
    ? costCenters.map((c) => ({ ...c, rows: chosen.filter((p) => costOf(p) === c.id) })).filter((g) => g.rows.length)
    : departments.map((d) => ({ ...d, rows: chosen.filter((p) => p.departmentId === d.id) })).filter((g) => g.rows.length);
  const activeCount = Object.values(org.filters).filter((v) => toList(v).length).length;
  const scopeLabel = activeCount === 1 && (deptName || costName) ? (deptName || costName) : activeCount ? "Filtered people" : "Search results";

  if (!cycle) return null;

  const save = async () => {
    if (!changes) { toast.info("Nothing changed."); return; }
    setBusy(true);
    try {
      const { data } = await api.updateCycleMembers(cycle.id, {
        add: toAdd.map((p) => p.id),
        restore: toRestore.map((p) => member(p)),
        remove: toRemove.map((p) => member(p)),
        applyTemplates,
        reason: toRemove.length ? "Employees added to / removed from the cycle by HR" : "Employees added to the cycle",
      });
      const parts = [
        data.added + data.restored ? `${data.added + data.restored} added` : "",
        data.removed ? `${data.removed} removed` : "",
      ].filter(Boolean);
      if (parts.length) toast.success(`${cycle.name}: ${parts.join(", ")}.`);
      if (data.failed.length) {
        toast.error(`${data.failed.length} could not be changed: ${data.failed.map((f) => `${all.find((p) => p.id === f.employeeId)?.name || f.employeeId} (${getErrorMessage(f.error)})`).join("; ")}`);
      }
      onDone();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      size="xl"
      title={`Employees in ${cycle.name}`}
      subtitle="Ticked = in the cycle, unticked = not in the cycle. Use the filters (Business Unit, Department, Location, Cost Centre, Legal Entity) or search a name: only matching employees are listed. Tick or untick them (or Select all), change the filters, then Save changes."
      onClose={busy ? undefined : onClose}
      footer={
        <>
          <span className="muted modal-footer-note">
            {selected.length} ticked
          </span>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button icon={FiSave} onClick={save} loading={busy} disabled={!changes}>Save changes</Button>
        </>
      }
    >
      {people === null ? <Loader /> : (
        <div className="cycle-picker">
          <div className="form-stack">
            <div className="cycle-picker-dropdowns">
              {/* [UPDATED] Business Unit, Department, Location, Cost Centre, Legal Entity: combine them to narrow the list */}
              <OrgFilters org={orgUi} marks={marks} markLabel="in cycle" />
            </div>
            <div className="cycle-picker-toolbar">
              <SearchInput value={filters.search} onChange={set("search")} placeholder="Find by name, employee ID or email" />
              <label className="checkbox">
                <input type="checkbox" checked={onlyInCycle} onChange={(e) => showInCycle(e.target.checked)} />
                <span>Show only employees in the cycle</span>
              </label>
              {/* [NEW] list the employees who are not in the cycle (not ticked), so they can be found and added */}
              <label className="checkbox">
                <input type="checkbox" checked={onlyNotInCycle} onChange={(e) => showNotInCycle(e.target.checked)} />
                <span>Show employees not in the cycle</span>
              </label>
              {/* [NEW] deselect every employee in the cycle at once */}
              <Button variant="secondary" size="sm" icon={FiUserMinus} onClick={() => setConfirmClear(true)} disabled={busy || !clearable.length}>
                Deselect all employees ({clearable.length})
              </Button>
            </div>

            {/* [NEW] Import employees from a CSV or Excel file (to add or to remove) */}
            <div className="cycle-picker-import">
              <input ref={fileRef} type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="sr-only" onChange={importFile} aria-label="Import employees file" />
              <div className="row-actions">
                <Button variant="secondary" size="sm" icon={FiUpload} onClick={pickFile} disabled={busy}>Import CSV / Excel</Button>
                <Button variant="ghost" size="sm" icon={FiDownload} onClick={() => downloadTemplate("xlsx")}>Excel template</Button>
                <Button variant="ghost" size="sm" icon={FiDownload} onClick={() => downloadTemplate("csv")}>CSV template</Button>
              </div>
              <p className="form-hint">One column named employee_id (or email), one employee per row. The file only ticks people here; nothing is saved until you click Save changes.</p>
              {report?.error && <Banner tone="danger" title="Couldn't read the file">{report.error}</Banner>}
              {report && !report.error && (
                <Banner tone={report.unmatched.length || report.locked.length ? "warning" : "success"} title={`${report.fileName}: ${report.applied} of ${report.total} added to the cycle selection`}>
                  {report.unchanged > 0 && <div>{report.unchanged} already in the cycle.</div>}
                  {report.duplicates > 0 && <div>{report.duplicates} duplicate row{report.duplicates === 1 ? "" : "s"} ignored.</div>}
                  {report.locked.length > 0 && <div>Cannot be changed ({report.locked.length}): {report.locked.join("; ")}.</div>}
                  {report.unmatched.length > 0 && <div>Not found or inactive ({report.unmatched.length}): {report.unmatched.slice(0, 20).join(", ")}{report.unmatched.length > 20 ? ` and ${report.unmatched.length - 20} more` : ""}.</div>}
                </Banner>
              )}
            </div>

            <ul className="cycle-picker-list">
              <li className="cycle-picker-all">
                <label className="checkbox cycle-picker-row">
                  <input type="checkbox" checked={allHere} disabled={!changeable.length} onChange={toggleAllHere} aria-label="Select all" />
                  <span className="cell-stack">
                    <strong>Select all ({changeable.length})</strong>
                    <small>{chosenFilter ? `${onlyNotInCycle && !org.active && !q ? "Employees not in the cycle" : scopeLabel} · ${tickedHere} of ${changeable.length} ticked` : "Choose a filter, search a name, or tick “Show only employees in the cycle” / “Show employees not in the cycle”"}</small>
                  </span>
                </label>
              </li>
              {shown.map((p) => {
                const lock = lockReason(p);
                return (
                  <li key={p.id} className={lock ? "is-mapped" : ""}>
                    <label className="checkbox cycle-picker-row">
                      <input type="checkbox" checked={selected.includes(p.id)} disabled={Boolean(lock)} onChange={() => toggle(p.id)} aria-label={`Select ${p.name}`} />
                      <span className="cycle-picker-name"><strong>{p.name}</strong> <span className="muted">({p.id})</span></span>
                      {inCycle(p) && !lock && <span className="tag tag-success" title="Already added to this cycle">In cycle</span>}
                      {lock && <span className="tag tag-warning" title="This person cannot be changed here">{lock}</span>}
                    </label>
                  </li>
                );
              })}
              {!shown.length && <li className="muted">{chosenFilter ? (onlyNotInCycle ? "Everyone matching is already in the cycle." : "No employees in this department / cost center.") : "Choose a Department or a Cost center to see its employees."}</li>}
            </ul>

            {toAdd.length > 0 && (
              <label className="checkbox">
                <input type="checkbox" checked={applyTemplates} onChange={(e) => setApplyTemplates(e.target.checked)} />
                <span>Apply the approved role template of each designation to the new people (the reviewing manager&apos;s team template first, otherwise the organization-wide one)</span>
              </label>
            )}
            {launched && toAdd.length > 0 && (
              <Banner tone="info" title="This cycle is already launched">
                People you add now start in goal setting straight away. Everyone needs an active reporting manager (their reviewer).
              </Banner>
            )}
          </div>

          <aside className="cycle-picker-selected" aria-label="Employees in the cycle after saving">
            <h4>In the cycle ({selected.length})</h4>
            <div className="segmented cycle-picker-group-by" role="radiogroup" aria-label="Group by">
              <button type="button" role="radio" aria-checked={groupBy === "department"} className={groupBy === "department" ? "is-active" : ""} onClick={() => setGroupBy("department")}>By department</button>
              <button type="button" role="radio" aria-checked={groupBy === "costCenter"} className={groupBy === "costCenter" ? "is-active" : ""} onClick={() => setGroupBy("costCenter")}>By cost center</button>
            </div>
            {!selected.length && <p className="muted">Nobody yet. Choose a department or cost center and tick people.</p>}
            {groups.map((g) => (
              <div key={g.id} className="cycle-picker-group">
                <div className="cycle-picker-group-title"><span>{g.name}</span><span>{g.rows.length}</span></div>
                {g.rows.map((p) => (
                  <div key={p.id} className="cycle-picker-pill">
                    <span>{p.name}</span>
                    {canChange(p) && <button type="button" aria-label={`Remove ${p.name}`} onClick={() => toggle(p.id)}><FiX size={14} /></button>}
                  </div>
                ))}
              </div>
            ))}
          </aside>
        </div>
      )}

      {/* [NEW] Confirm before deselecting everyone */}
      <ConfirmDialog
        open={confirmClear}
        tone="danger"
        title="Deselect all employees?"
        confirmLabel="Deselect all"
        onConfirm={deselectAll}
        onClose={() => setConfirmClear(false)}
        message={`This unticks ${clearable.length} employee${clearable.length === 1 ? "" : "s"}, so they will be taken out of ${cycle.name} when you click Save changes. Their records, goals and KRAs are kept and come back if you tick them again.${lockedTicked > 0 ? ` ${lockedTicked} employee${lockedTicked === 1 ? "" : "s"} cannot be changed (for example, result finalized) and will stay.` : ""}`}
      />
    </Modal>
  );
}